import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Typography, MenuItem, Select, Paper, Container, Tabs, Tab, Grid, IconButton, alpha
} from '@mui/material';
import { styled } from '@mui/system';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation } from 'react-router';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { organizationSelectionError } from './organizationSelection';
import {
  firstIdentifierError,
  normalizeIndianCode,
  validateEmployeeIdentifiers,
} from './employeeFormValidation';
import { GlobalStateContext } from '../../context/empcontext';
import JoiningInfo from './JoiningInfo';
import { useCreateEmployee } from '../../hooks/useEmployees';
import { UPDATE_FINAL_STATUS } from '../../api';
import { StyledInput, Label, grey } from '../StyledComponents/StyledComponent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WorkIcon from '@mui/icons-material/Work';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CustomFieldsSection, {
  useCustomFieldDefinitions,
  validateCustomFieldValues,
} from './CustomFieldsSection';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const SlideContainer = styled(Box)(({ currentstep }) => ({
  display: 'flex',
  width: '300%',
  transform: `translateX(-${(currentstep * 100) / 3}%)`,
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const SlideItem = styled(Box)({
  width: '33.333%',
  flexShrink: 0,
});

const TabPanel = styled(Box)({
  paddingTop: '32px',
  overflow: 'hidden',
  width: '100%',
});

const CustomSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  height: '40px',
  backgroundColor: theme.palette.mode === 'dark' ? alpha(grey[900], 0.4) : '#fff',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha(grey[700], 0.5) : '#e5e7eb'}`,
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  boxShadow: `0 1px 2px ${alpha('#000', 0.02)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
      border: `1px solid ${ACCENT}`,
  },
  '&.Mui-focused': {
      border: `1px solid ${ACCENT}`,
      backgroundColor: '#fff',
  }
}));

const ActionFab = styled(Button)(() => ({
  borderRadius: '12px',
  padding: '10px 28px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.9rem',
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg,#2f6bff,#2557d6)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  marginLeft: '12px',
  marginRight: '8px',
}));

const CreateNewEmp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const { formData, setFormData } = useContext(GlobalStateContext);
  const { mutate: createEmployee, isPending: isLoading } = useCreateEmployee();
  const [successData, setSuccessData] = useState(null);
  const customFields = useCustomFieldDefinitions();

  useEffect(() => {
    // When arriving from a "Joined" candidate, prefill their details.
    const prefill = location.state?.prefill || {};
    setFormData({
      employee_no: '',
      name: '',
      date_of_joining: dayjs().format('YYYY-MM-DD'),
      gender: '',
      date_of_birth: '',
      email: '',
      status: '',
      confirmation_date: null,
      reporting_manager: '',
      manager_id: '',
      reporting_manager_id: null,
      mobile_number: '',
      employment_status: '',
      pan_no: '',
      uan_no: '',
      bank_account_no: '',
      nationality: '',
      location: '',
      designation: '',
      department: '',
      designation_id: null,
      department_id: null,
      grade: '',
      attendance_schema: '',
      level_cost_center: '',
      branch_name: '',
      branch_id: null,
      division: '',
      ifsc_code: '',
      aadhaar_number: '',
      custom_attributes: {},
      ...prefill,
    });
  }, [setFormData, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = ['pan_no', 'ifsc_code'].includes(name)
      ? normalizeIndianCode(value)
      : value;
    setFormData(prev => ({ ...prev, [name]: normalizedValue }));
  };

  const handleDateChange = (newDate, name) => {
    // Format to YYYY-MM-DD for backend consistency, but UI will show DD/MM/YYYY
    const formattedDate = newDate ? dayjs(newDate).format('YYYY-MM-DD') : '';
    setFormData(prev => ({ ...prev, [name]: formattedDate }));
  };

  const nextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Mandatory Field Validation
    const mandatoryFields = {
        'employee_no': 'Employee No',
        'name': 'Full Name',
        'email': 'Email',
        'mobile_number': 'Mobile Number',
        'status': 'Status',
        'gender': 'Gender',
        'location': 'Region / Location',
        'branch_name': 'Branch Name',
        'employment_status': 'Employment Status',
        'attendance_schema': 'Attendance Schema',
        'team_head': 'Team Head'
    };

    const emptyFields = Object.entries(mandatoryFields)
        .filter(([key]) => !formData[key] || formData[key] === '')
        .map(([, label]) => label);

    if (emptyFields.length > 0) {
        toast.error(`Please fill mandatory fields: ${emptyFields.join(', ')}`);
        return;
    }

    const organizationError = organizationSelectionError(formData);
    if (organizationError) {
      toast.error(organizationError);
      return;
    }

    const identifierError = firstIdentifierError(formData);
    if (identifierError) {
      toast.error(identifierError);
      return;
    }

    const customErrors = validateCustomFieldValues(
      customFields.definitions,
      formData.custom_attributes,
    );
    if (Object.keys(customErrors).length > 0) {
      const firstDefinition = customFields.definitions.find(
        (item) => customErrors[item.field_name]
      );
      toast.error(`${firstDefinition?.field_label || 'Custom field'}: ${Object.values(customErrors)[0]}`);
      return;
    }

    createEmployee(formData, {
      onSuccess: (data) => {
        setSuccessData(data);
        // If we came here from a "Joined" candidate, mark them Joined now that
        // the employee record is actually created (was Pending until now).
        const candidateName = location.state?.candidateName;
        if (candidateName) {
          UPDATE_FINAL_STATUS({ name: candidateName, status: 'joined' }).catch((e) =>
            console.error('Failed to mark candidate joined:', e)
          );
        }
      },
    });
  };

  const identifierErrors = validateEmployeeIdentifiers(formData);

  const renderForm = (step) => {
    switch (step) {
      case 0:
        return (
          <Paper elevation={0} sx={{ ...CARD_SX }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon sx={{ color: ACCENT }} />
                Personal Information
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Enter the employee's basic identity and contact details.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Label>Employee No *</Label>
                <StyledInput
                  name="employee_no"
                  value={formData.employee_no}
                  onChange={handleInputChange}
                  placeholder="EMP-000"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Full Name *</Label>
                <StyledInput
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Legal Name"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Aadhaar Number</Label>
                <StyledInput
                  name="aadhaar_number"
                  value={formData.aadhaar_number}
                  onChange={handleInputChange}
                  placeholder="12 Digit Aadhaar No"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Phone Number</Label>
                <StyledInput
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  placeholder="+91 0000000000"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Email Address</Label>
                <StyledInput
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@company.com"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Gender</Label>
                <CustomSelect
                  name="gender"
                  value={formData.gender || ''}
                  onChange={handleInputChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select Gender</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Others</MenuItem>
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Date of Birth</Label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                    onChange={(val) => handleDateChange(val, 'date_of_birth')}
                    format="DD/MM/YYYY"
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            sx: {
                                '& .MuiOutlinedInput-root': {
                                    height: '40px',
                                    borderRadius: '12px',
                                    bgcolor: '#fff',
                                    '& fieldset': { border: '1px solid #e5e7eb' },
                                    '&:hover fieldset': { borderColor: ACCENT },
                                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                                }
                            }
                        }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Paper>
        );

      case 1:
        return (
          <Paper elevation={0} sx={{ ...CARD_SX }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AccountBalanceIcon sx={{ color: ACCENT }} />
                Bank & Identification
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Securely store financial and statutory identifiers.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Label>Nationality</Label>
                <StyledInput
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  placeholder="Indian"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>Bank Account No</Label>
                <StyledInput
                  name="bank_account_no"
                  value={formData.bank_account_no}
                  onChange={handleInputChange}
                  placeholder="Account Number"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>IFSC Code</Label>
                <StyledInput
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="HDFC0001234"
                  inputProps={{ maxLength: 11, 'aria-invalid': Boolean(identifierErrors.ifsc_code) }}
                  sx={identifierErrors.ifsc_code ? { borderColor: '#ef4444' } : undefined}
                />
                {identifierErrors.ifsc_code && (
                  <Typography variant="caption" color="error">{identifierErrors.ifsc_code}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>PAN Number</Label>
                <StyledInput
                  name="pan_no"
                  value={formData.pan_no}
                  onChange={handleInputChange}
                  placeholder="ABCDE1234F"
                  inputProps={{ maxLength: 10, 'aria-invalid': Boolean(identifierErrors.pan_no) }}
                  sx={identifierErrors.pan_no ? { borderColor: '#ef4444' } : undefined}
                />
                {identifierErrors.pan_no && (
                  <Typography variant="caption" color="error">{identifierErrors.pan_no}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>UAN Number</Label>
                <StyledInput
                  name="uan_no"
                  value={formData.uan_no}
                  onChange={handleInputChange}
                  placeholder="Universal Account Number"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Label>ESI Number</Label>
                <StyledInput
                  name="ESIno"
                  value={formData.ESIno}
                  onChange={handleInputChange}
                  placeholder="ESI Registration No"
                />
              </Grid>
            </Grid>
          </Paper>
        );

      case 2:
        return (
          <Paper elevation={0} sx={{ ...CARD_SX }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WorkIcon sx={{ color: ACCENT }} />
                Joining Information
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Finalize company placement and employment terms.
              </Typography>
            </Box>
            <JoiningInfo />
            <Box sx={{ mt: 3 }}>
              <CustomFieldsSection
                definitions={customFields.definitions}
                loading={customFields.loading}
                error={customFields.error}
                values={formData.custom_attributes || {}}
                onChange={(customAttributes) => setFormData((current) => ({
                  ...current, custom_attributes: customAttributes,
                }))}
              />
            </Box>
          </Paper>
        );
      default: return null;
    }
  };

  if (successData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ ...CARD_SX, textAlign: 'center', p: { xs: 4, md: 6 } }}>
          <Box sx={{ mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#16a34a' }} />
          </Box>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, color: '#111827' }}>
            Success!
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 500, color: '#6b7280' }}>
            Employee record for <strong>{formData.name}</strong> has been created.
          </Typography>

          <Box sx={{ p: 3, mb: 4, bgcolor: '#f8fafc', borderRadius: '16px', border: '1px solid #eef0f4' }}>
            <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1.5, fontWeight: 700, textTransform: 'uppercase' }}>
              Document Portal URL
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', p: 2, borderRadius: '12px', border: `1px solid ${alpha(ACCENT, 0.3)}` }}>
              <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontWeight: 700, color: ACCENT, textAlign: 'left' }}>
                {successData.form_url}
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  navigator.clipboard.writeText(successData.form_url);
                  alert("URL copied!");
                }}
                sx={{ ...GRADIENT_BTN }}
              >
                Copy
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => window.open(successData.form_url, '_blank')}
              sx={{ ...GRADIENT_BTN, px: 4, py: 1.5 }}
            >
              Open Onboarding Portal
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/employees')}
              sx={{ ...OUTLINED_BTN, px: 4, py: 1.5 }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        <Box sx={{ mb: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/employees')}
            sx={{ ...OUTLINED_BTN, color: '#6b7280', '&:hover': { color: ACCENT } }}
          >
            Back to Employee List
          </Button>
        </Box>

        {/* Header card */}
        <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PersonAddIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Add New Employee
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Onboard a new team member by completing the details below.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{
          mb: { xs: 2, md: 3 },
          position: 'sticky',
          top: 20,
          zIndex: 10,
          ...CARD_SX,
          p: 0.5,
          display: 'flex',
          alignItems: 'center'
        }}>
          <IconButton
            onClick={prevStep}
            disabled={currentStep === 0}
            sx={{ mr: 1, color: ACCENT, '&.Mui-disabled': { opacity: 0.3 } }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <Tabs
            value={currentStep}
            onChange={(e, v) => setCurrentStep(v)}
            variant="fullWidth"
            sx={{
              flexGrow: 1,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.9rem',
                color: '#6b7280',
                minHeight: '56px',
                borderRadius: '16px',
                transition: 'all 0.3s',
                '&.Mui-selected': {
                    color: ACCENT,
                    bgcolor: `${ACCENT}0d`,
                }
              },
              '& .MuiTabs-indicator': { display: 'none' }
            }}
          >
            <Tab label="1. Personal Details" />
            <Tab label="2. Bank & ID" />
            <Tab label="3. Joining Information" />
          </Tabs>

          <ActionFab
            variant="contained"
            onClick={nextStep}
            disabled={isLoading}
            disableElevation
            startIcon={currentStep === 2 ? <CheckCircleIcon /> : <ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
          >
            {isLoading ? 'Processing...' : currentStep < 2 ? 'Continue' : 'Finalize & Create'}
          </ActionFab>
        </Box>

        <TabPanel>
          <SlideContainer currentstep={currentStep}>
            <SlideItem>{renderForm(0)}</SlideItem>
            <SlideItem>{renderForm(1)}</SlideItem>
            <SlideItem>{renderForm(2)}</SlideItem>
          </SlideContainer>
        </TabPanel>

        {/* ToastContainer removed to use the global one in index.js */}
      </Container >
    </Box >
  );
};

export default CreateNewEmp;
